import { useState } from 'react';
import { Link } from 'react-router';
import Logo from '@/components/Logo';
import { useDebounced } from '@/lib/catalogue';
import { useCustomers } from '@/lib/orders';
import { cn } from '@/lib/utils';
import RewardBalance from './admin/RewardBalance';
import ClientOrders from './ClientOrders';
import CouponsClaims from './CouponsClaims';

const TABS = [
  { key: 'cadeaux', label: 'Solde des cadeaux' },
  { key: 'commandes', label: 'Commandes du client' },
  { key: 'coupons', label: 'Coupons de tirage' },
] as const;
type TabKey = (typeof TABS)[number]['key'];

/**
 * Espace « Consultation client » du représentant super : sans quitter son rôle de
 * vente, il vérifie le solde des cadeaux d'un client, l'historique de ses commandes
 * et ses coupons. On cherche le client UNE SEULE FOIS ; les trois onglets portent
 * ensuite tous sur ce client sans le ressaisir.
 */
export default function Consultation() {
  const [tab, setTab] = useState<TabKey>('cadeaux');
  const [customer, setCustomer] = useState<{ id: number; name: string; custNum: string } | null>(
    null,
  );
  const [q, setQ] = useState('');
  const debounced = useDebounced(q, 250);
  const recherche = debounced.trim() !== '';
  const clients = useCustomers(debounced, recherche);

  return (
    <div className="min-h-full">
      <header className="bg-card border-b">
        <div className="mx-auto flex max-w-4xl items-center justify-between gap-4 p-4">
          <div className="flex items-center gap-4">
            <Logo className="h-9" />
            <h1 className="text-xl font-bold">Consultation client</h1>
          </div>
          <Link
            to="/"
            className="border-input flex h-10 items-center rounded-lg border px-4 text-sm font-medium"
          >
            ← Accueil
          </Link>
        </div>
        {/* Onglets actifs seulement une fois un client choisi. */}
        {customer && (
          <nav className="mx-auto flex max-w-4xl gap-1 px-4">
            {TABS.map((t) => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={cn(
                  'border-b-2 px-4 py-3 text-sm font-medium whitespace-nowrap transition-colors',
                  tab === t.key
                    ? 'border-primary text-foreground'
                    : 'text-muted-foreground border-transparent hover:text-foreground',
                )}
              >
                {t.label}
              </button>
            ))}
          </nav>
        )}
      </header>

      <main className="mx-auto max-w-4xl p-6">
        {customer === null ? (
          // Recherche UNIQUE du client, partagée ensuite par les trois onglets.
          <div className="space-y-4">
            <div>
              <h2 className="text-lg font-semibold">Rechercher un client</h2>
              <p className="text-muted-foreground text-sm">
                Trouvez le client une seule fois, puis consultez son solde de cadeaux, ses commandes
                et ses coupons sans avoir à le ressaisir.
              </p>
            </div>
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Nom, n° de client ou ville…"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              autoFocus
              className="border-input bg-background h-12 w-full max-w-lg rounded-lg border px-3 text-base"
            />

            {recherche && (
              <div className="grid max-w-lg gap-2">
                {clients.data?.customers.map((c) => (
                  <button
                    key={c.id}
                    onClick={() => setCustomer({ id: c.id, name: c.name, custNum: c.custNum })}
                    className="bg-card hover:border-primary/50 flex items-center gap-3 rounded-lg border p-3 text-left"
                  >
                    <span className="bg-secondary text-secondary-foreground rounded px-2 py-0.5 font-mono text-xs font-medium">
                      {c.custNum}
                    </span>
                    <span className="font-medium">{c.name}</span>
                  </button>
                ))}
                {!clients.isPending && clients.data?.customers.length === 0 && (
                  <p className="text-muted-foreground text-sm">Aucun client trouvé.</p>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-5">
            {/* Bandeau client partagé par les trois onglets. */}
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className="text-lg font-semibold">{customer.name}</h2>
                <p className="text-muted-foreground font-mono text-sm">{customer.custNum}</p>
              </div>
              <button
                onClick={() => {
                  setCustomer(null);
                  setQ('');
                }}
                className="border-input rounded-lg border px-4 py-2 text-sm font-medium"
              >
                ← Autre client
              </button>
            </div>

            {tab === 'cadeaux' ? (
              <RewardBalance customerId={customer.id} />
            ) : tab === 'commandes' ? (
              <ClientOrders customerId={customer.id} />
            ) : (
              // Le rep super PEUT distribuer les coupons (contrairement au solde
              // cadeaux, en lecture seule).
              <CouponsClaims customerId={customer.id} />
            )}
          </div>
        )}
      </main>
    </div>
  );
}
